import RecordedSportStats from "../components/RecordedSportStats";
// src/pages/NbaGameDetail.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { API_BASE } from "../api";
import { isAllowedPublicEdge } from "../utils/publicEdge";
import MatchupModelSection from "../components/MatchupModelSection";
const api = (p) => `${API_BASE}${p}`;

/* ── colour palette ────────────────────────────────────────── */
const C = {
  pageBg:     "#fff5ed",          // warm cream/peach wash
  cardBg:     "#ffffff",
  courtLine:  "#d97706",          // orange court lines
  courtKey:   "#fb923c",          // lighter orange for key/paint
  threePoint: "#ea580c",          // darker orange for 3pt arc
  accentDark: "#c2410c",          // burnt orange – headings, active tab
  accentLight:"#fed7aa",          // light orange pill / badge bg
  textPri:    "#1e293b",
  textSub:    "#475569",
  textMut:    "#94a3b8",
  green:      "#16a34a",
  greenLt:    "rgba(22,163,74,0.10)",
  red:        "#dc2626",
  redLt:      "rgba(220,38,38,0.10)",
};

/* ── SVG: basketball court background (fixed, behind everything) ──── */
function CourtBg() {
  return (
    <svg
      viewBox="0 0 800 400"
      preserveAspectRatio="xMidYMid slice"
      style={{
        position: "fixed", inset: 0, width: "100%", height: "100%",
        opacity: 0.12, pointerEvents: "none", zIndex: 0,
      }}
    >
      {/* outer court boundary */}
      <rect x="40" y="40" width="720" height="320"
            fill="none" stroke={C.courtLine} strokeWidth="3" />

      {/* ── left basket area ── */}
      {/* key/paint */}
      <rect x="40" y="140" width="100" height="120"
            fill="none" stroke={C.courtKey} strokeWidth="2.5" />
      {/* free throw circle */}
      <circle cx="140" cy="200" r="36"
              fill="none" stroke={C.courtKey} strokeWidth="2" />
      {/* backboard */}
      <line x1="40" y1="180" x2="40" y2="220"
            stroke={C.courtLine} strokeWidth="4" />
      {/* rim */}
      <circle cx="52" cy="200" r="9"
              fill="none" stroke={C.courtLine} strokeWidth="2.5" />

      {/* ── right basket area ── */}
      {/* key/paint */}
      <rect x="660" y="140" width="100" height="120"
            fill="none" stroke={C.courtKey} strokeWidth="2.5" />
      {/* free throw circle */}
      <circle cx="660" cy="200" r="36"
              fill="none" stroke={C.courtKey} strokeWidth="2" />
      {/* backboard */}
      <line x1="760" y1="180" x2="760" y2="220"
            stroke={C.courtLine} strokeWidth="4" />
      {/* rim */}
      <circle cx="748" cy="200" r="9"
              fill="none" stroke={C.courtLine} strokeWidth="2.5" />

      {/* ── center circle ── */}
      <circle cx="400" cy="200" r="36"
              fill="none" stroke={C.courtLine} strokeWidth="2" />
      <line x1="400" y1="40" x2="400" y2="360"
            stroke={C.courtLine} strokeWidth="2" />

      {/* ── three-point arcs ── */}
      {/* left 3pt arc */}
      <path d="M 40,80 Q 180,80 180,200 Q 180,320 40,320"
            fill="none" stroke={C.threePoint} strokeWidth="2" />
      {/* right 3pt arc */}
      <path d="M 760,80 Q 620,80 620,200 Q 620,320 760,320"
            fill="none" stroke={C.threePoint} strokeWidth="2" />
    </svg>
  );
}

/* ── SVG: basketball icon ──── */
function Basketball({ size = 24 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
      <circle cx="12" cy="12" r="10" fill="#ea580c" stroke="#c2410c" strokeWidth="1.5" />
      <path d="M12,2 Q16,12 12,22" fill="none" stroke="#c2410c" strokeWidth="1" />
      <path d="M12,2 Q8,12 12,22" fill="none" stroke="#c2410c" strokeWidth="1" />
      <path d="M2,12 Q12,8 22,12" fill="none" stroke="#c2410c" strokeWidth="1" />
      <path d="M2,12 Q12,16 22,12" fill="none" stroke="#c2410c" strokeWidth="1" />
    </svg>
  );
}

export default function NbaGameDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    setErr("");
    setData(null);
    const url = api(`/api/fixtures/id/${id}/json`);
    fetch(url, { cache: "no-store" })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((j) => setData(j))
      .catch((e) => setErr(e.message));
  }, [id]);

  const groupedOdds = useMemo(() => {
    const byBook = {};
    const rows = data?.odds || [];
    for (const o of rows) {
      const book = (o.bookmaker || "other").toLowerCase();
      if (!byBook[book]) byBook[book] = [];
      byBook[book].push(o);
    }
    for (const b of Object.keys(byBook)) {
      byBook[b].sort(
        (a, b) =>
          (a.market || "").localeCompare(b.market || "") ||
          (b.last_seen || "").localeCompare(a.last_seen || "")
      );
    }
    return byBook;
  }, [data]);

  const formatUTC = (iso) => {
    if (!iso) return "—";
    const s = /Z$|[+-]\d{2}:\d{2}$/.test(iso) ? iso : iso + "Z";
    try {
      return new Date(s).toLocaleString("en-GB", {
        timeZone: "UTC",
        weekday: "short",
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  if (err) {
    return (
      <div style={{ position: "relative", minHeight: "100vh", background: C.pageBg }}>
        <CourtBg />
        <div style={{ position: "relative", zIndex: 1, padding: 24 }}>
          <p style={{ color: C.red }}>{err}</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div style={{ position: "relative", minHeight: "100vh", background: C.pageBg }}>
        <CourtBg />
        <div style={{ position: "relative", zIndex: 1, padding: 24 }}>
          <p>Loading…</p>
        </div>
      </div>
    );
  }

  if (!data.fixture) {
    return (
      <div style={{ position: "relative", minHeight: "100vh", background: C.pageBg }}>
        <CourtBg />
        <div style={{ position: "relative", zIndex: 1, padding: 24 }}>
          <p>No fixture data found.</p>
        </div>
      </div>
    );
  }

  const f = data.fixture;
  const bestEdges = (data.best_edges || []).filter((edge) => isAllowedPublicEdge(edge?.edge));

  return (
    <div style={{ position: "relative", minHeight: "100vh", background: C.pageBg }}>
      <CourtBg />

      <div style={{ position: "relative", zIndex: 1, maxWidth: 960, margin: "0 auto", padding: "24px 16px" }}>
        {/* Back link */}
        <div style={{ marginBottom: 16 }}>
          <Link
            to="/basketball"
            style={{
              color: C.accentDark,
              textDecoration: "none",
              fontSize: 14,
              fontWeight: 500,
            }}
          >
            ← Back to NBA Games
          </Link>
        </div>

        {/* Header card */}
        <div
          style={{
            background: C.cardBg,
            borderRadius: 12,
            padding: 24,
            marginBottom: 20,
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
          }}
        >
          {/* Competition badge */}
          <div style={{ marginBottom: 12 }}>
            <span
              style={{
                display: "inline-block",
                background: C.accentLight,
                color: C.accentDark,
                padding: "4px 10px",
                borderRadius: 6,
                fontSize: 12,
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              {f.comp || "NBA"}
            </span>
          </div>

          {/* Teams + basketball */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 16,
              fontSize: 26,
              fontWeight: 700,
              color: C.textPri,
              marginBottom: 12,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "flex-end" }}>
              <span>{f.home_team}</span>
              {f.home_logo ? (
                <img
                  src={f.home_logo}
                  alt={f.home_team}
                  width={42}
                  height={42}
                  style={{ objectFit: "contain" }}
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
              ) : (
                <Basketball size={30} />
              )}
            </div>

            <span style={{ color: C.textMut, fontWeight: 400, fontSize: 20 }}>vs</span>

            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {f.away_logo ? (
                <img
                  src={f.away_logo}
                  alt={f.away_team}
                  width={42}
                  height={42}
                  style={{ objectFit: "contain" }}
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
              ) : (
                <Basketball size={30} />
              )}
              <span>{f.away_team}</span>
            </div>
          </div>

          {/* Kickoff time */}
          <div style={{ textAlign: "center", color: C.textSub, fontSize: 14 }}>
            {formatUTC(f.kickoff_utc)} UTC
          </div>
        </div>

        <RecordedSportStats fixtureId={id} sport="nba" />
        <MatchupModelSection
          fixtureId={id}
          sport="nba"
          fixture={f}
          homeStats={data.home_stats}
          awayStats={data.away_stats}
          predictions={data.predictions}
          modelProbs={data.model_probs}
          theme={{
            accent: C.accentDark,
            accentSoft: C.accentLight,
            accentBorder: "rgba(194,65,12,0.18)",
            surface: "rgba(255,255,255,0.92)",
            text: C.textPri,
            textMuted: C.textSub,
            border: "rgba(194,65,12,0.12)",
            positive: C.green,
            negative: C.red,
          }}
        />

        {/* Best Edges */}
        <div
          style={{
            background: C.cardBg,
            borderRadius: 12,
            padding: 24,
            marginBottom: 20,
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
          }}
        >
          <h3 style={{ margin: "0 0 16px", color: C.accentDark, fontSize: 18 }}>
            🏀 Best Edges
          </h3>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: `2px solid ${C.courtLine}` }}>
                <th align="left" style={{ padding: "8px 0", color: C.textSub, fontWeight: 600, fontSize: 13 }}>
                  Market
                </th>
                <th align="right" style={{ padding: "8px 0", color: C.textSub, fontWeight: 600, fontSize: 13 }}>
                  Odds
                </th>
                <th align="right" style={{ padding: "8px 0", color: C.textSub, fontWeight: 600, fontSize: 13 }}>
                  Edge
                </th>
                <th align="left" style={{ padding: "8px 0", color: C.textSub, fontWeight: 600, fontSize: 13 }}>
                  Bookmaker
                </th>
              </tr>
            </thead>
            <tbody>
              {bestEdges.map((e, i) => (
                <tr key={i} style={{ borderBottom: `1px solid ${C.accentLight}` }}>
                  <td style={{ padding: "10px 0", color: C.textPri, fontWeight: 500 }}>{e.market}</td>
                  <td align="right" style={{ padding: "10px 0", color: C.textPri }}>{Number(e.price).toFixed(2)}</td>
                  <td
                    align="right"
                    style={{
                      padding: "10px 0",
                      color: C.green,
                      fontWeight: 600,
                    }}
                  >
                    +{(Number(e.edge) * 100).toFixed(1)}%
                  </td>
                  <td style={{ padding: "10px 0", color: C.textSub, textTransform: "capitalize" }}>{e.bookmaker}</td>
                </tr>
              ))}
              {bestEdges.length === 0 && (
                <tr>
                  <td colSpan={4} style={{ padding: "16px 0", color: C.textMut, textAlign: "center" }}>
                    No edges found yet. Check back closer to tipoff.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Team Statistics */}
        {(data.home_stats || data.away_stats) && (
          <div
            style={{
              background: C.cardBg,
              borderRadius: 12,
              padding: 24,
              marginBottom: 20,
              boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
            }}
          >
            <h3 style={{ margin: "0 0 16px", color: C.accentDark, fontSize: 18 }}>
              📈 Team Statistics
            </h3>
            <div style={{ display: "grid", gap: 16 }}>
              {/* Home Team Stats */}
              {data.home_stats && (
                <div style={{
                  border: `1px solid ${C.accentLight}`,
                  borderRadius: 12,
                  padding: 16,
                }}>
                  <div style={{
                    fontSize: 15,
                    fontWeight: 700,
                    color: C.accentDark,
                    marginBottom: 12,
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}>
                    {data.fixture.home_logo && (
                      <img src={data.fixture.home_logo} alt="" width={20} height={20} style={{ objectFit: "contain" }} />
                    )}
                    {data.fixture.home_team}
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, fontSize: 13 }}>
                    <div>
                      <div style={{ color: C.textMut, marginBottom: 2 }}>Points For/Game</div>
                      <div style={{ fontSize: 20, fontWeight: 700, color: C.textPri }}>
                        {data.home_stats.gf_avg?.toFixed(1) || "—"}
                      </div>
                    </div>
                    <div>
                      <div style={{ color: C.textMut, marginBottom: 2 }}>Points Against/Game</div>
                      <div style={{ fontSize: 20, fontWeight: 700, color: C.textPri }}>
                        {data.home_stats.ga_avg?.toFixed(1) || "—"}
                      </div>
                    </div>
                    <div>
                      <div style={{ color: C.textMut, marginBottom: 2 }}>Win Rate</div>
                      <div style={{ fontSize: 20, fontWeight: 700, color: C.textPri }}>
                        {data.home_stats.win_rate ? `${(data.home_stats.win_rate * 100).toFixed(1)}%` : "—"}
                      </div>
                    </div>
                    <div>
                      <div style={{ color: C.textMut, marginBottom: 2 }}>Point Diff/Game</div>
                      <div style={{ fontSize: 20, fontWeight: 700, color: data.home_stats.gd_per_game >= 0 ? C.green : C.red }}>
                        {data.home_stats.gd_per_game ? data.home_stats.gd_per_game.toFixed(1) : "—"}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Away Team Stats */}
              {data.away_stats && (
                <div style={{
                  border: `1px solid ${C.accentLight}`,
                  borderRadius: 12,
                  padding: 16,
                }}>
                  <div style={{
                    fontSize: 15,
                    fontWeight: 700,
                    color: C.accentDark,
                    marginBottom: 12,
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}>
                    {data.fixture.away_logo && (
                      <img src={data.fixture.away_logo} alt="" width={20} height={20} style={{ objectFit: "contain" }} />
                    )}
                    {data.fixture.away_team}
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, fontSize: 13 }}>
                    <div>
                      <div style={{ color: C.textMut, marginBottom: 2 }}>Points For/Game</div>
                      <div style={{ fontSize: 20, fontWeight: 700, color: C.textPri }}>
                        {data.away_stats.gf_avg?.toFixed(1) || "—"}
                      </div>
                    </div>
                    <div>
                      <div style={{ color: C.textMut, marginBottom: 2 }}>Points Against/Game</div>
                      <div style={{ fontSize: 20, fontWeight: 700, color: C.textPri }}>
                        {data.away_stats.ga_avg?.toFixed(1) || "—"}
                      </div>
                    </div>
                    <div>
                      <div style={{ color: C.textMut, marginBottom: 2 }}>Win Rate</div>
                      <div style={{ fontSize: 20, fontWeight: 700, color: C.textPri }}>
                        {data.away_stats.win_rate ? `${(data.away_stats.win_rate * 100).toFixed(1)}%` : "—"}
                      </div>
                    </div>
                    <div>
                      <div style={{ color: C.textMut, marginBottom: 2 }}>Point Diff/Game</div>
                      <div style={{ fontSize: 20, fontWeight: 700, color: data.away_stats.gd_per_game >= 0 ? C.green : C.red }}>
                        {data.away_stats.gd_per_game ? data.away_stats.gd_per_game.toFixed(1) : "—"}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* All Odds */}
        <div
          style={{
            background: C.cardBg,
            borderRadius: 12,
            padding: 24,
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
          }}
        >
          <h3 style={{ margin: "0 0 16px", color: C.accentDark, fontSize: 18 }}>
            📊 All Odds
          </h3>
          {Object.keys(groupedOdds).length === 0 ? (
            <p style={{ color: C.textMut }}>No odds captured yet.</p>
          ) : (
            Object.entries(groupedOdds).map(([book, rows]) => (
              <div key={book} style={{ marginBottom: 24 }}>
                <div
                  style={{
                    fontWeight: 700,
                    marginBottom: 8,
                    textTransform: "capitalize",
                    color: C.textPri,
                    fontSize: 15,
                  }}
                >
                  {book}
                </div>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ borderBottom: `1px solid ${C.accentLight}` }}>
                      <th align="left" style={{ padding: "6px 0", color: C.textSub, fontWeight: 500, fontSize: 12 }}>
                        Market
                      </th>
                      <th align="right" style={{ padding: "6px 0", color: C.textSub, fontWeight: 500, fontSize: 12 }}>
                        Price
                      </th>
                      <th align="left" style={{ padding: "6px 0", color: C.textSub, fontWeight: 500, fontSize: 12 }}>
                        Last Seen
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((o, idx) => {
                      const seen = o.last_seen
                        ? /Z$|[+-]\d{2}:\d{2}$/.test(o.last_seen)
                          ? o.last_seen
                          : o.last_seen + "Z"
                        : "";
                      return (
                        <tr key={`${book}-${idx}`} style={{ borderBottom: `1px solid #f1f5f9` }}>
                          <td style={{ padding: "8px 0", color: C.textPri, fontSize: 14 }}>{o.market}</td>
                          <td align="right" style={{ padding: "8px 0", color: C.textPri, fontSize: 14 }}>
                            {Number(o.price).toFixed(2)}
                          </td>
                          <td style={{ padding: "8px 0", color: C.textMut, fontSize: 13 }}>
                            {seen ? new Date(seen).toLocaleString("en-GB") : "—"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ))
          )}
        </div>

        <p style={{ fontSize: 13, color: C.textMut, marginTop: 16, textAlign: "center" }}>
          🧠 Advanced analytics, player props, and AI-powered insights coming soon.
        </p>
      </div>
    </div>
  );
}
